import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { UserPlus, Check, X, Clock, User, Mail, Phone, Building2, GraduationCap, Users, ChevronDown, ClipboardCheck, Anchor } from 'lucide-react';
import { ROLES_PRO } from './login/ChoixRolePro';
import { ChangerRoleDemande } from './registrations/ChangerRoleDemande';

const ADDITIONAL_ROLES = [
  { value: 'formateur', label: 'Formateur' },
  { value: 'tuteur', label: 'Tuteur' },
  { value: 'entreprise', label: 'Entreprise' },
  { value: 'responsable_pedagogique', label: 'Resp. Pédagogique' },
];

const roleIcons = {
  stagiaire: <GraduationCap className="h-4 w-4" />,
  candidat: <GraduationCap className="h-4 w-4" />,
  tuteur: <User className="h-4 w-4" />,
  formateur: <Users className="h-4 w-4" />,
  entreprise: <Building2 className="h-4 w-4" />,
  responsable_pedagogique: <GraduationCap className="h-4 w-4" />,
  moniteur_vnm: <Anchor className="h-4 w-4" />,
  evaluateur: <ClipboardCheck className="h-4 w-4" />
};

const roleLabels = {
  stagiaire: 'Stagiaire',
  candidat: 'Candidat',
  ...Object.fromEntries(ROLES_PRO.map(r => [r.value, r.label]))
};
const roleDesc = Object.fromEntries(ROLES_PRO.map(r => [r.value, r.desc]));

const roleColors = {
  stagiaire: 'bg-blue-100 text-blue-700 border-blue-200',
  candidat: 'bg-sky-100 text-sky-700 border-sky-200',
  tuteur: 'bg-orange-100 text-orange-700 border-orange-200',
  formateur: 'bg-green-100 text-green-700 border-green-200',
  entreprise: 'bg-purple-100 text-purple-700 border-purple-200',
  responsable_pedagogique: 'bg-teal-100 text-teal-700 border-teal-200',
  moniteur_vnm: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  evaluateur: 'bg-rose-100 text-rose-700 border-rose-200'
};

const PendingRegistrations = () => {
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [processingId, setProcessingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [extraRoles, setExtraRoles] = useState({});

  const toggleExtraRole = (userId, role) => {
    setExtraRoles(prev => {
      const current = prev[userId] || [];
      return { ...prev, [userId]: current.includes(role) ? current.filter(r => r !== role) : [...current, role] };
    });
  };

  useEffect(() => {
    loadRegistrations();
  }, []);

  const loadRegistrations = async () => {
    try {
      const response = await axios.get('/pending-registrations');
      setRegistrations(response.data || []);
    } catch (error) {
      toast.error('Erreur lors du chargement des inscriptions');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (userId, name) => {
    setProcessingId(userId);
    try {
      await axios.post(`/pending-registrations/${userId}/approve`);
      
      const selected = extraRoles[userId] || [];
      for (const role of selected) {
        try {
          await axios.post(`/users/${userId}/add-role`, { role });
        } catch (e) { /* rôle déjà existant */ }
      }
      
      const rolesMsg = selected.length > 0
        ? ` + ${selected.map(r => ADDITIONAL_ROLES.find(ar => ar.value === r)?.label || r).join(', ')}`
        : '';
      toast.success(`${name} validé${rolesMsg}`);
      setRegistrations(prev => prev.filter(r => r.user_id !== userId));
      setExpandedId(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la validation');
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (userId, name) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir refuser l'inscription de ${name} ?`)) {
      return;
    }
    
    setProcessingId(userId);
    try {
      await axios.post(`/pending-registrations/${userId}/reject`);
      toast.success(`Inscription de ${name} refusée`);
      // Retirer de la liste
      setRegistrations(prev => prev.filter(r => r.user_id !== userId));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du refus');
    } finally {
      setProcessingId(null);
    }
  };

  // Filtrer par rôle
  const filteredRegistrations = filter === 'all' 
    ? registrations 
    : registrations.filter(r => r.role === filter);

  // Compter par rôle
  const countByRole = {
    all: registrations.length,
    candidat: registrations.filter(r => r.role === 'candidat').length,
    stagiaire: registrations.filter(r => r.role === 'stagiaire').length,
    tuteur: registrations.filter(r => r.role === 'tuteur').length,
    formateur: registrations.filter(r => r.role === 'formateur').length,
    entreprise: registrations.filter(r => r.role === 'entreprise').length,
    responsable_pedagogique: registrations.filter(r => r.role === 'responsable_pedagogique').length,
    moniteur_vnm: registrations.filter(r => r.role === 'moniteur_vnm').length,
    evaluateur: registrations.filter(r => r.role === 'evaluateur').length
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#8DC63F]"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden" data-testid="pending-registrations">
      {/* Header */}
      <div className="p-6 border-b border-slate-200">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-[#8DC63F]" />
            <h2 className="text-xl font-display font-semibold text-[#0F172A]">
              Inscriptions en attente
            </h2>
            {registrations.length > 0 && (
              <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-sm font-medium rounded-full">
                {registrations.length}
              </span>
            )}
          </div>
        </div>

        {/* Filtres par rôle */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setFilter('all')}
            className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
              filter === 'all' 
                ? 'bg-[#8DC63F] text-white border-[#8DC63F]' 
                : 'bg-white text-slate-600 border-slate-200 hover:border-[#8DC63F]'
            }`}
          >
            Tous ({countByRole.all})
          </button>
          {['candidat', 'stagiaire', 'tuteur', 'formateur', 'evaluateur', 'entreprise', 'responsable_pedagogique', 'moniteur_vnm'].filter(r => countByRole[r] > 0).map(role => (
            <button
              key={role}
              onClick={() => setFilter(role)}
              className={`px-3 py-1.5 text-sm rounded-full border transition-colors flex items-center gap-1 ${
                filter === role 
                  ? roleColors[role] + ' border-current' 
                  : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
              }`}
            >
              {roleIcons[role]}
              {roleLabels[role]} ({countByRole[role]})
            </button>
          ))}
        </div>
      </div>

      {/* Liste */}
      <div className="divide-y divide-slate-100">
        {filteredRegistrations.length === 0 ? (
          <div className="p-8 text-center">
            <Clock className="h-12 w-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">
              {filter === 'all' 
                ? 'Aucune inscription en attente'
                : `Aucune inscription ${roleLabels[filter]} en attente`}
            </p>
          </div>
        ) : (
          filteredRegistrations.map(registration => (
            <div 
              key={registration.user_id} 
              className="p-4 hover:bg-slate-50 transition-colors"
              data-testid={`registration-${registration.user_id}`}
            >
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                {/* Info utilisateur */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span title={roleDesc[registration.role]} className={`px-2 py-0.5 text-xs font-medium rounded-full border flex items-center gap-1 ${roleColors[registration.role] || 'bg-slate-100 text-slate-600 border-slate-200'}`} data-testid={`role-badge-${registration.user_id}`}>
                      {roleIcons[registration.role] || <User className="h-4 w-4" />}
                      {roleLabels[registration.role] || registration.role}
                    </span>
                    <span className="text-xs text-slate-400">
                      {formatDate(registration.created_at)}
                    </span>
                    {roleDesc[registration.role] && (
                      <ChangerRoleDemande
                        registration={registration}
                        onChange={(role) => setRegistrations(prev => prev.map(r => r.user_id === registration.user_id ? { ...r, role } : r))}
                      />
                    )}
                  </div>
                  
                  <h3 className="font-semibold text-[#0F172A]">
                    {registration.prenom} {registration.nom}
                  </h3>
                  
                  <div className="flex flex-wrap gap-3 mt-1 text-sm text-slate-600">
                    <span className="flex items-center gap-1">
                      <Mail className="h-3.5 w-3.5" />
                      <span className="truncate max-w-[200px]">{registration.email}</span>
                    </span>
                    {registration.telephone && (
                      <span className="flex items-center gap-1">
                        <Phone className="h-3.5 w-3.5" />
                        {registration.telephone}
                      </span>
                    )}
                    {registration.annee_diplome && (
                      <span className="flex items-center gap-1 text-indigo-600">
                        <Clock className="h-3.5 w-3.5" />
                        Promo {registration.annee_diplome}
                      </span>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-col items-stretch sm:items-end gap-2">
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleReject(registration.user_id, `${registration.prenom} ${registration.nom}`)}
                      disabled={processingId === registration.user_id}
                      className="text-red-600 border-red-200 hover:bg-red-50"
                      data-testid={`btn-reject-${registration.user_id}`}
                    >
                      <X className="h-4 w-4 mr-1" />
                      Refuser
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => setExpandedId(expandedId === registration.user_id ? null : registration.user_id)}
                      className="bg-[#8DC63F] hover:bg-[#7db336]"
                      data-testid={`btn-validate-expand-${registration.user_id}`}
                    >
                      <Check className="h-4 w-4 mr-1" />
                      Valider
                      <ChevronDown className={`h-3.5 w-3.5 ml-1 transition-transform ${expandedId === registration.user_id ? 'rotate-180' : ''}`} />
                    </Button>
                  </div>
                  
                  {/* Panneau attribution de rôles */}
                  {expandedId === registration.user_id && (
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 w-full min-w-[280px]" data-testid={`role-panel-${registration.user_id}`}>
                      <p className="text-xs font-semibold text-slate-600 mb-2">Attribuer des rôles supplémentaires :</p>
                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {ADDITIONAL_ROLES.filter(r => r.value !== registration.role).map(r => {
                          const selected = (extraRoles[registration.user_id] || []).includes(r.value);
                          return (
                            <button
                              key={r.value}
                              onClick={() => toggleExtraRole(registration.user_id, r.value)}
                              className={`px-2.5 py-1 text-xs font-medium rounded-full border transition-colors ${
                                selected 
                                  ? 'bg-[#8DC63F] text-white border-[#8DC63F]' 
                                  : 'bg-white text-slate-600 border-slate-300 hover:border-[#8DC63F] hover:text-[#8DC63F]'
                              }`}
                              data-testid={`toggle-role-${r.value}-${registration.user_id}`}
                            >
                              {selected && <Check className="h-3 w-3 inline mr-1" />}
                              {r.label}
                            </button>
                          );
                        })}
                      </div>
                      <Button
                        size="sm"
                        onClick={() => handleApprove(registration.user_id, `${registration.prenom} ${registration.nom}`)}
                        disabled={processingId === registration.user_id}
                        className="w-full bg-[#8DC63F] hover:bg-[#7db336]"
                        data-testid={`btn-confirm-approve-${registration.user_id}`}
                      >
                        {processingId === registration.user_id ? (
                          <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                        ) : (
                          <>
                            <Check className="h-4 w-4 mr-1" />
                            Confirmer la validation
                            {(extraRoles[registration.user_id] || []).length > 0 && 
                              ` + ${(extraRoles[registration.user_id] || []).length} rôle(s)`
                            }
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default PendingRegistrations;
